import { RefObject } from "react";
import { twMerge } from "tailwind-merge";
import clsx from "clsx";

export function mapSet<T, R>(set: Set<T>, callback: (value: T) => R): R[] {
  return Array.from(set).map(callback);
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout | null;

  return function debounced(...args: Parameters<T>): void {
    clearTimeout(timeoutId!);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

/**
 *
 * @param classes A list of string (style classes from tailwind)
 * @returns a concatenated string
 */

export function cn(...inputs: (string | undefined)[]) {
  // Merge class names
  return twMerge(clsx(inputs));
}

/**
 * Generates a unique identifier for each toast.
 * @param count a number to keep track of counting
 * @returns {string} A unique string representation of the ID.
 */

export function genId(count: number) {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

export const getTimeRemaining = (e: string) => {
  const total = Date.parse(e) - Date.parse(String(new Date()));

  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / 1000 / 60) % 60);
  const hours = Math.floor((total / 1000 / 60 / 60) % 24);
  return {
    total,
    hours,
    minutes,
    seconds,
  };
};
export const startTimer = (
  e: string,
  setTimer: (time: string | false) => void,
) => {
  let { total, hours, minutes, seconds } = getTimeRemaining(e);
  if (total >= 0) {
    if (hours > 0) {
      setTimer(
        (hours > 9 ? hours : "0" + hours) +
          ":" +
          (minutes > 9 ? minutes : "0" + minutes) +
          ":" +
          (seconds > 9 ? seconds : "0" + seconds),
      );
    } else {
      setTimer(
        (minutes > 9 ? minutes : "0" + minutes) +
          ":" +
          (seconds > 9 ? seconds : "0" + seconds),
      );
    }
  } else {
    setTimer(false);
  }
};
export function scrollToElement(
  ref: React.RefObject<HTMLElement | null> | string,
  time: number = 500,
  offset: number = 0,
): Promise<number | false> {
  const Quad_easeIn = (t: number, b: number, c: number, d: number): number =>
    c * ((t = t / d - 1) * t * t + 1) + b;
  const requestAnimationFrame = window.requestAnimationFrame;

  let targetElement: HTMLElement | null = null;

  if (typeof ref === "string") {
    targetElement = document.getElementById(ref);
    if (!targetElement) {
      console.error(`Element with id '${ref}' not found.`);
      return Promise.resolve(false);
    }
  } else if (ref.current) {
    targetElement = ref.current;
  } else {
    console.error("Invalid ref object.");
    return Promise.resolve(false);
  }

  const end =
    targetElement.getBoundingClientRect().top + window.scrollY + offset;

  let scrollTop = window.scrollY || document.documentElement.scrollTop;

  if (scrollTop === end) {
    return Promise.resolve(false);
  }

  let b = scrollTop;
  let c = end - b;
  let d = time;
  let start: number | null = null;

  return new Promise((resolve) => {
    function step(timeStamp: number) {
      if (start === null) start = timeStamp;
      let progress = timeStamp - start;
      if (progress < time) {
        let st = Quad_easeIn(progress, b, c, d);
        document.body.scrollTop = st;
        document.documentElement.scrollTop = st;
        requestAnimationFrame(step);
      } else {
        document.body.scrollTop = end;
        document.documentElement.scrollTop = end;
        resolve(end);
      }
    }
    requestAnimationFrame(step);
  });
}

export function findSimilarObjectsInArray<X>(array: X[], criteria: keyof X) {
  const pureArray: ({
    count: number;
  } & X)[] = [];

  for (let x of array) {
    const itemsCount = array
      .filter((item) => item[criteria] === x[criteria])
      .reduce((acc) => acc + 1, 0);

    if (!pureArray.some((item) => item[criteria] === x[criteria]))
      pureArray.push({
        ...x,
        count: itemsCount,
      });
  }
  return pureArray;
}

type deviceType = "desktop" | "mobile" | "tablet";
export function getUserAgentDevice(requestHeaders: Headers): deviceType {
  const userAgent = requestHeaders.get("user-agent");
  let deviceType: deviceType = "desktop";

  if (userAgent) {
    if (/mobile/i.test(userAgent)) {
      deviceType = "mobile";
    } else if (/tablet/i.test(userAgent)) {
      deviceType = "tablet";
    }
  }

  return deviceType;
}

export function getUserAgentDeviceWithNavigator(): deviceType | null {
  if (typeof window !== "undefined") {
    const userAgent = window.navigator.userAgent;
    let deviceType: deviceType = "desktop";

    if (userAgent) {
      if (/mobile/i.test(userAgent)) {
        deviceType = "mobile";
      } else if (/tablet/i.test(userAgent)) {
        deviceType = "tablet";
      }
    }
    return deviceType;
  }

  return null;
}

export function getProperty<X, Z extends keyof X>(obj: X, key: Z): X[Z] {
  return obj[key];
}
